import { useQuery } from '@tanstack/react-query';
import { pnidApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { PnidAIData } from '../types';

export interface AiDataState {
  hasAIData: boolean;
  aiData: PnidAIData | null;
  loading: boolean;
}

/**
 * Whether a drawing has AI extraction output, and the payload when loadable.
 * Always attempts getAIData after the exists probe so signed-URL-only drawings
 * still fill dropdowns.
 */
export function useAiData(docId: string | null | undefined): AiDataState {
  const query = useQuery({
    queryKey: queryKeys.aiData(docId ?? ''),
    queryFn: async (): Promise<{ exists: boolean; data: PnidAIData | null }> => {
      const result = await pnidApi.checkAIDataExists(docId!);
      try {
        const data = await pnidApi.getAIData(docId!);
        return { exists: result.exists, data };
      } catch (e) {
        console.warn('Could not load AI data:', e);
        return { exists: result.exists, data: null };
      }
    },
    enabled: Boolean(docId),
  });

  return {
    hasAIData: query.data?.exists ?? false,
    aiData: query.data?.data ?? null,
    loading: query.isPending && Boolean(docId),
  };
}
